import React, { useState, useEffect, useContext } from 'react';
import { 
  Activity, Circle, Moon, Coffee, MessageCircle, 
  Plus, Trash2, Check, X, Zap, Loader,
  Heart, Star, Smile, Music, Gamepad2, Film, Book, Dumbbell,
  Sparkles, Flame, Users, Home, Briefcase, GraduationCap, Plane, ShoppingCart,
  Calendar, Clock, Edit2, Power, PowerOff
} from 'lucide-react';
import { AdminAuthContext } from '../../context/AdminAuthContext';
import { useSocket } from '../../context/SocketContext';
import * as statusService from '../../services/statusService';
import { useAlert } from '../../components/ui/alert/useAlert';
import Select from '../../components/ui/Select';
import CheckBox from '../../components/ui/CheckBox';
import Modal from '../../components/ui/Modal';

const StatusManagement = () => {
  const { admin } = useContext(AdminAuthContext);
  const { showSuccess, showError, showWarning, showConfirm } = useAlert();
  const { connect, disconnect, joinBeccoRoom, on, connected } = useSocket();
  
  // Stati
  const [currentStatus, setCurrentStatus] = useState('disponibile');
  const [customMessage, setCustomMessage] = useState('');
  const [customStatuses, setCustomStatuses] = useState([]);
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [editingCustomStatus, setEditingCustomStatus] = useState(null);
  const [newCustomStatus, setNewCustomStatus] = useState({
    label: '',
    message: '',
    icon: 'Star',
    color: '#EC4899'
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Stati per la programmazione
  const [schedules, setSchedules] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleFormData, setScheduleFormData] = useState({
    scheduleName: '',
    status: 'disponibile',
    customStatusId: '',
    customMessage: '',
    daysOfWeek: [],
    startTime: '',
    endTime: '',
    isActive: true,
    restorePreviousStatus: false
  });

  // Giorni della settimana per la programmazione
  const DAYS = [
    { value: 0, label: 'Dom' },
    { value: 1, label: 'Lun' },
    { value: 2, label: 'Mar' },
    { value: 3, label: 'Mer' },
    { value: 4, label: 'Gio' },
    { value: 5, label: 'Ven' },
    { value: 6, label: 'Sab' }
  ];

  // Icone disponibili per gli stati personalizzati
  const availableIcons = [
    { name: 'Star', component: Star },
    { name: 'Heart', component: Heart },
    { name: 'Smile', component: Smile },
    { name: 'Music', component: Music },
    { name: 'Gamepad2', component: Gamepad2 },
    { name: 'Film', component: Film },
    { name: 'Book', component: Book },
    { name: 'Dumbbell', component: Dumbbell },
    { name: 'Sparkles', component: Sparkles },
    { name: 'Flame', component: Flame },
    { name: 'Users', component: Users },
    { name: 'Home', component: Home },
    { name: 'Briefcase', component: Briefcase },
    { name: 'GraduationCap', component: GraduationCap },
    { name: 'Plane', component: Plane },
    { name: 'ShoppingCart', component: ShoppingCart }
  ];

  // 16 colori predefiniti stile Google
  const availableColors = [
    { name: 'Pink', value: '#EC4899' },
    { name: 'Purple', value: '#A855F7' },
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Cyan', value: '#06B6D4' },
    { name: 'Teal', value: '#14B8A6' },
    { name: 'Green', value: '#10B981' },
    { name: 'Lime', value: '#84CC16' },
    { name: 'Yellow', value: '#EAB308' },
    { name: 'Orange', value: '#F97316' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Rose', value: '#F43F5E' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Violet', value: '#8B5CF6' },
    { name: 'Fuchsia', value: '#D946EF' },
    { name: 'Emerald', value: '#059669' },
    { name: 'Amber', value: '#F59E0B' }
  ];

  // Stati predefiniti con configurazione completa
  const standardStatuses = [
    {
      id: 'disponibile',
      label: 'Disponibile',
      icon: Circle,
      bgClass: 'bg-green-500/20 dark:bg-green-400/20',
      textClass: 'text-green-600 dark:text-green-400',
      iconClass: 'text-green-600 dark:text-green-400',
      description: 'Libero e pronto per qualsiasi richiesta'
    },
    {
      id: 'occupato',
      label: 'Occupato',
      icon: Zap,
      bgClass: 'bg-orange-500/20 dark:bg-orange-400/20',
      textClass: 'text-orange-600 dark:text-orange-400',
      iconClass: 'text-orange-600 dark:text-orange-400',
      description: 'Impegnato ma disponibile per urgenze'
    },
    {
      id: 'non_disturbare',
      label: 'Non Disturbare',
      icon: Moon,
      bgClass: 'bg-red-500/20 dark:bg-red-400/20',
      textClass: 'text-red-600 dark:text-red-400',
      iconClass: 'text-red-600 dark:text-red-400',
      description: 'Solo per emergenze vere'
    },
    {
      id: 'afk',
      label: 'AFK',
      icon: Coffee,
      bgClass: 'bg-yellow-500/20 dark:bg-yellow-600/20',
      textClass: 'text-yellow-700 dark:text-yellow-400',
      iconClass: 'text-yellow-700 dark:text-yellow-400',
      description: 'Momentaneamente lontano dalla tastiera'
    },
    {
      id: 'in_pausa',
      label: 'In Pausa',
      icon: MessageCircle,
      bgClass: 'bg-blue-500/20 dark:bg-blue-400/20',
      textClass: 'text-blue-600 dark:text-blue-400',
      iconClass: 'text-blue-600 dark:text-blue-400',
      description: 'Pausa pranzo o relax, torno presto'
    }
  ];

  // Carica dati iniziali
  useEffect(() => {
    if (admin?.id) {
      loadData();
      
      // Connetti al WebSocket come admin
      const token = localStorage.getItem('adminToken');
      if (token) {
        console.log('🔌 Admin connecting to WebSocket...');
        connect(token, true); // true = isAdmin
      }
    }
    
    return () => {
      disconnect();
    };
  }, [admin?.id, connect, disconnect]);

  // Effetto per joinare la room del Becco quando connesso
  useEffect(() => {
    if (admin?.id && connected) {
      console.log('🔌 Admin joining Becco room:', admin.id);
      joinBeccoRoom(admin.id);
    }
  }, [admin?.id, connected, joinBeccoRoom]);

  // Effetto per ascoltare aggiornamenti di stato via WebSocket
  useEffect(() => {
    if (!admin?.id) return;

    console.log('🔔 Admin setting up status-updated listener');

    const handleStatusUpdate = async (data) => {
      console.log('🔔 Admin received status update:', data);
      
      // Determina il valore dello stato
      let statusValue;
      if (data.status === 'custom' && data.customStatusId) {
        statusValue = `custom_${data.customStatusId}`;
      } else if (data.status === 'custom' && data.customStatus) {
        statusValue = `custom_${data.customStatus.id}`;
      } else {
        statusValue = data.status.replace(/-/g, '_');
      }
      
      console.log('🔔 Admin updating status to:', statusValue);
      setCurrentStatus(statusValue);
      setCustomMessage(data.customMessage || '');
    };

    const unsubscribe = on('status-updated', handleStatusUpdate);

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [admin?.id, on]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCurrentStatus(),
        loadCustomStatuses(),
        loadSchedules()
      ]);
    } catch (error) {
      console.error('Errore caricamento dati:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carica stato corrente
  const loadCurrentStatus = async () => {
    try {
      const response = await statusService.getCurrentStatus(admin.id);
      console.log('📊 Admin status loaded:', response);
      const data = response.data; // Estrai data dalla risposta
      console.log('📊 Status data:', data);
      
      // Verifica che data.status esista
      if (!data || !data.status) {
        console.error('❌ Status data is missing or invalid:', data);
        return;
      }
      
      // Determina il valore dello stato
      let statusValue;
      if (data.status === 'custom' && data.customStatusId) {
        // Usa customStatusId se disponibile
        statusValue = `custom_${data.customStatusId}`;
      } else if (data.status === 'custom' && data.customStatus) {
        // Fallback al vecchio metodo
        statusValue = `custom_${data.customStatus.id}`;
      } else {
        // Stato standard: converti trattini in underscore
        statusValue = data.status.replace(/-/g, '_');
      }
      
      console.log('📊 Setting currentStatus to:', statusValue);
      setCurrentStatus(statusValue);
      setCustomMessage(data.customMessage || '');
    } catch (error) {
      console.error('Errore caricamento stato:', error);
    }
  };

  // Carica stati personalizzati
  const loadCustomStatuses = async () => {
    try {
      const data = await statusService.getCustomStatuses(admin.id);
      console.log('🔧 Loaded custom statuses:', data);
      data.forEach(status => {
        console.log(`🔧 Status "${status.label}": icon=${status.icon}, color=${status.color}`);
      });
      setCustomStatuses(data);
    } catch (error) {
      console.error('Errore caricamento stati custom:', error);
    }
  };

  // Handler per cambiare stato
  const handleStatusChange = async (statusId, customStatusId = null) => {
    if (updating) return;
    
    try {
      setUpdating(true);
      
      // Controlla se c'è uno schedule attualmente attivo
      console.log('📅 Checking for active schedules...', schedules);
      const activeSchedule = schedules.find(s => {
        console.log(`📅 Schedule: ${s.scheduleName}, isActive: ${s.isActive}, isActiveNow: ${s.isActiveNow}`);
        return s.isActive && s.isActiveNow;
      });
      
      console.log('📅 Active schedule found:', activeSchedule);
      
      if (activeSchedule) {
        // Mostra warning e chiedi conferma
        console.log('⚠️ Showing confirmation dialog...');
        const confirmed = await showConfirm({
          title: 'Programmazione Rilevata',
          message: `Il programma "${activeSchedule.scheduleName}" è attualmente attivo. Cambiando manualmente lo stato, il programma verrà disattivato automaticamente. Vuoi continuare?`,
          confirmText: 'Sì, cambia stato',
          cancelText: 'Annulla'
        });
        
        console.log('✅ User confirmed:', confirmed);
        
        if (!confirmed) {
          setUpdating(false);
          return;
        }
        
        // Disattiva il programma
        try {
          console.log('🔄 Disabling program...');
          await statusService.updateSchedule(activeSchedule.id, { isActive: false });
          showWarning(`Programma "${activeSchedule.scheduleName}" disattivato. Puoi riattivarlo manualmente dalla sezione programmazione.`);
          // Ricarica gli schedule
          await loadSchedules();
        } catch (error) {
          console.error('Errore disattivazione programma:', error);
        }
      }
      
      // Converti underscore in trattini per il backend
      const backendStatus = statusId.replace(/_/g, '-');
      const payload = {
        adminId: admin.id,
        status: backendStatus,
        // Se è uno stato custom, non usare customMessage (lo stato custom ha già il suo message)
        // Se è uno stato standard, usa customMessage solo se è stato inserito manualmente
        customMessage: (statusId === 'custom' || !customMessage) ? null : customMessage
      };
      
      // Se è un programma custom, aggiungi l'ID
      if (statusId === 'custom' && customStatusId) {
        payload.customStatusId = customStatusId;
      }
      
      await statusService.updateStatus(payload);
      showSuccess('Stato aggiornato con successo');
      // Ricarica lo stato completo dal backend
      await loadCurrentStatus();
    } catch (error) {
      console.error('Errore aggiornamento stato:', error);
      showError('Errore nell\'aggiornamento dello stato');
    } finally {
      setUpdating(false);
    }
  };

  // Handler per aggiungere stato personalizzato
  const handleAddCustomStatus = async () => {
    if (!newCustomStatus.label.trim()) return;

    try {
      const payload = {
        adminId: admin.id,
        label: newCustomStatus.label.trim(),
        icon: newCustomStatus.icon || 'Star',
        color: newCustomStatus.color || '#EC4899',
        message: newCustomStatus.message.trim() || null
      };
      console.log('💾 Saving custom program with:', payload);
      const created = await statusService.createCustomStatus(payload);
      console.log('✅ Created custom program:', created.customStatus);
      
      setCustomStatuses([...customStatuses, created.customStatus]);
      setNewCustomStatus({ label: '', message: '', icon: 'Star', color: '#EC4899' });
      setIsAddingCustom(false);
      showSuccess('Stato personalizzato creato con successo');
    } catch (error) {
      console.error('Errore creazione stato custom:', error);
      showError('Errore nella creazione dello stato');
    }
  };

  // Handler per modificare programma personalizzato
  const handleEditCustomStatus = async () => {
    if (!newCustomStatus.label.trim() || !editingCustomStatus) return;

    try {
      const payload = {
        label: newCustomStatus.label.trim(),
        icon: newCustomStatus.icon || 'Star',
        color: newCustomStatus.color || '#EC4899',
        message: newCustomStatus.message.trim() || null
      };
      
      await statusService.updateCustomStatus(editingCustomStatus.id, payload);
      
      setCustomStatuses(customStatuses.map(s => 
        s.id === editingCustomStatus.id 
          ? { ...s, ...payload } 
          : s
      ));
      
      setNewCustomStatus({ label: '', message: '', icon: 'Star', color: '#EC4899' });
      setEditingCustomStatus(null);
      setIsAddingCustom(false);
      showSuccess('Stato personalizzato aggiornato con successo');
    } catch (error) {
      console.error('Errore modifica stato custom:', error);
      showError('Errore nell\'aggiornamento dello stato');
    }
  };

  // Handler per iniziare la modifica
  const startEditCustomStatus = (status) => {
    setEditingCustomStatus(status);
    setNewCustomStatus({
      label: status.label,
      message: status.message || '',
      icon: status.icon || 'Star',
      color: status.color || '#EC4899'
    });
    setIsAddingCustom(true);
  };

  // Handler per eliminare stato personalizzato
  const handleDeleteCustomStatus = async (statusId) => {
    const confirmed = await showConfirm({
      title: 'Elimina Stato',
      message: 'Sei sicuro di voler eliminare questo stato personalizzato?',
      confirmText: 'Elimina',
      cancelText: 'Annulla'
    });
    
    if (!confirmed) return;

    try {
      await statusService.deleteCustomStatus(statusId, admin.id);
      setCustomStatuses(customStatuses.filter(s => s.id !== statusId));
      showSuccess('Stato personalizzato eliminato');
    } catch (error) {
      console.error('Errore eliminazione stato:', error);
      showError('Errore nell\'eliminazione dello stato');
    }
  };

  // Handler per eliminare tutti gli stati personalizzati
  const handleDeleteAllCustomStatuses = async () => {
    if (customStatuses.length === 0) {
      showWarning('Non ci sono stati personalizzati da eliminare');
      return;
    }

    const confirmed = await showConfirm({
      title: 'Elimina Tutti gli Stati',
      message: `Sei sicuro di voler eliminare tutti i ${customStatuses.length} stati personalizzati? Questa azione non può essere annullata.`,
      confirmText: 'Elimina Tutti',
      cancelText: 'Annulla'
    });
    
    if (!confirmed) return;

    try {
      // Elimina tutti gli stati in parallelo
      await Promise.all(
        customStatuses.map(status => 
          statusService.deleteCustomStatus(status.id, admin.id)
        )
      );
      
      setCustomStatuses([]);
      showSuccess(`Tutti gli stati personalizzati (${customStatuses.length}) sono stati eliminati`);
    } catch (error) {
      console.error('Errore eliminazione stati:', error);
      showError('Errore nell\'eliminazione degli stati');
      // Ricarica per sicurezza
      loadCustomStatuses();
    }
  };

  // ============= FUNZIONI PER LA PROGRAMMAZIONE STATI =============
  
  const loadSchedules = async () => {
    try {
      const data = await statusService.getSchedules(admin.id);
      setSchedules(data);
    } catch (error) {
      console.error('Error loading schedules:', error);
      showError('Errore nel caricamento delle programmazioni');
    }
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();

    // Validazione
    if (!scheduleFormData.scheduleName || !scheduleFormData.startTime || !scheduleFormData.endTime || scheduleFormData.daysOfWeek.length === 0) {
      showError('Compila tutti i campi obbligatori');
      return;
    }

    if (scheduleFormData.startTime >= scheduleFormData.endTime) {
      showError('L\'ora di inizio deve essere precedente all\'ora di fine');
      return;
    }

    if (scheduleFormData.status === 'custom' && !scheduleFormData.customStatusId) {
      showError('Seleziona uno stato personalizzato');
      return;
    }

    try {
      const payload = {
        ...scheduleFormData,
        customStatusId: scheduleFormData.status === 'custom' ? parseInt(scheduleFormData.customStatusId) : null
      };

      if (editingSchedule) {
        await statusService.updateSchedule(editingSchedule.id, payload);
        showSuccess('Programmazione aggiornata con successo');
      } else {
        await statusService.createSchedule(payload);
        showSuccess('Programmazione creata con successo');
      }

      resetScheduleForm();
      setShowScheduleModal(false);
      loadSchedules();
    } catch (error) {
      console.error('Error saving schedule:', error);
      showError(error.response?.data?.error || 'Errore nel salvataggio');
    }
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa programmazione?')) return;

    try {
      await statusService.deleteSchedule(id);
      showSuccess('Programmazione eliminata con successo');
      loadSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      showError('Errore nell\'eliminazione');
    }
  };

  const handleToggleScheduleActive = async (schedule) => {
    try {
      await statusService.updateSchedule(schedule.id, { isActive: !schedule.isActive });
      showSuccess(`Programmazione ${!schedule.isActive ? 'attivata' : 'disattivata'}`);
      loadSchedules();
    } catch (error) {
      console.error('Error toggling schedule:', error);
      showError('Errore nell\'aggiornamento dello stato');
    }
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setScheduleFormData({
      scheduleName: schedule.scheduleName,
      status: schedule.status,
      customStatusId: schedule.customStatusId || '',
      customMessage: schedule.customMessage || '',
      daysOfWeek: schedule.daysOfWeek,
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      isActive: schedule.isActive,
      restorePreviousStatus: schedule.restorePreviousStatus
    });
    setShowScheduleModal(true);
  };

  const resetScheduleForm = () => {
    setScheduleFormData({
      scheduleName: '',
      status: 'disponibile',
      customStatusId: '',
      customMessage: '',
      daysOfWeek: [],
      startTime: '',
      endTime: '',
      isActive: true,
      restorePreviousStatus: false
    });
    setEditingSchedule(null);
  };

  const toggleDay = (dayValue) => {
    setScheduleFormData(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(dayValue)
        ? prev.daysOfWeek.filter(d => d !== dayValue)
        : [...prev.daysOfWeek, dayValue]
    }));
  };

  const formatNextExecution = (schedule) => {
    if (!schedule.nextExecution) return 'N/A';
    const date = new Date(schedule.nextExecution);
    return date.toLocaleString('it-IT', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDayLabel = (dayValue) => DAYS.find(d => d.value === dayValue)?.label || dayValue;

  const getStatusLabelForSchedule = (status) => {
    const statusMap = {
      'disponibile': 'Disponibile',
      'occupato': 'Occupato',
      'non-disturbare': 'Non Disturbare',
      'afk': 'AFK',
      'in-pausa': 'In Pausa'
    };
    return statusMap[status] || status;
  };

  const getStatusColorForSchedule = (status) => {
    const colorMap = {
      'disponibile': 'bg-green-500',
      'occupato': 'bg-orange-500',
      'non-disturbare': 'bg-red-500',
      'afk': 'bg-yellow-500',
      'in-pausa': 'bg-blue-500'
    };
    return colorMap[status] || 'bg-gray-500';
  };

  // ============= FINE FUNZIONI PROGRAMMAZIONE =============

  // Trova lo stato corrente
  const getCurrentStatusInfo = () => {
    console.log('Current status:', currentStatus);
    console.log('Standard statuses IDs:', standardStatuses.map(s => s.id));
    
    const standard = standardStatuses.find(s => s.id === currentStatus);
    if (standard) {
      console.log('Found standard status:', standard);
      return standard;
    }
    
    // Controlla se è uno stato custom (formato: custom_ID o solo ID numerico)
    const customId = currentStatus?.toString().startsWith('custom_') 
      ? parseInt(currentStatus.split('_')[1]) 
      : currentStatus;
    
    const custom = customStatuses.find(s => s.id === customId);
    if (custom) {
      console.log('Found custom status:', custom);
      // Trova l'icona corrispondente
      const StatusIcon = availableIcons.find(i => i.name === custom.icon)?.component || Star;
      const statusColor = custom.color || '#EC4899';
      
      return {
        label: custom.label,
        icon: StatusIcon,
        description: custom.message,
        bgClass: 'bg-light-surface-container dark:bg-dark-surface-container',
        textClass: 'text-light-on-surface dark:text-dark-on-surface',
        iconClass: '',
        color: statusColor
      };
    }
    
    console.log('Status not found, returning null');
    return null;
  };

  const activeStatus = getCurrentStatusInfo();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="w-8 h-8 animate-spin text-light-primary dark:text-dark-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-surface dark:bg-dark-surface p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-4 sm:p-6
          animate-fade-in opacity-0"
          style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        <div className="flex items-center gap-2 sm:gap-3">
          <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-light-primary dark:text-dark-primary" />
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-light-on-surface dark:text-dark-on-surface">
              Stato Becco
            </h2>
            <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
              Comunica la tua disponibilità
            </p>
          </div>
        </div>
      </div>

      {/* Stato Corrente - Preview Grande */}
      <div className={`
        ${activeStatus?.bgClass || 'bg-light-surface-container dark:bg-dark-surface-container'}
        rounded-lg shadow-surface-container p-4 sm:p-8
        animate-fade-in opacity-0
      `}
      style={{
        animationDelay: '200ms', 
        animationFillMode: 'forwards',
        ...(activeStatus?.color && { borderLeftColor: activeStatus.color, borderLeftWidth: '6px' })
      }}>
        <div className="text-center">
          <p className="text-xs sm:text-sm font-medium text-light-on-surface-variant dark:text-dark-on-surface-variant mb-2">
            STATO ATTUALE
          </p>
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-2 sm:mb-4">
            {activeStatus?.icon && (
              <activeStatus.icon 
                className={`w-8 h-8 sm:w-12 sm:h-12 ${activeStatus.iconClass}`}
                style={activeStatus.color ? { color: activeStatus.color } : {}}
              />
            )}
            <h3 
              className={`text-xl sm:text-3xl font-bold ${activeStatus?.textClass || 'text-light-on-surface dark:text-dark-on-surface'}`}
              style={activeStatus?.color ? { color: activeStatus.color } : {}}
            >
              {activeStatus?.label || 'Nessuno stato'}
            </h3>
          </div>
          {activeStatus?.description && (
            <p className="text-xs sm:text-base text-light-on-surface-variant dark:text-dark-on-surface-variant">
              {activeStatus.description}
            </p>
          )}
          {customMessage && (
            <div className="mt-2 sm:mt-4 p-2 sm:p-3 bg-light-surface dark:bg-dark-surface rounded-lg">
              <p className="text-xs sm:text-sm text-light-on-surface dark:text-dark-on-surface italic">
                "{customMessage}"
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Stati Standardizzati */}
      <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-4 sm:p-6
        animate-fade-in opacity-0"
        style={{animationDelay: '300ms', animationFillMode: 'forwards'}}>
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-light-primary dark:text-dark-primary" />
          <h3 className="text-base sm:text-lg font-semibold text-light-on-surface dark:text-dark-on-surface">
            Stati Predefiniti
          </h3>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
          {standardStatuses.map((status, index) => {
            const Icon = status.icon;
            const isActive = currentStatus === status.id;
            
            return (
              <button
                key={status.id}
                onClick={() => handleStatusChange(status.id)}
                disabled={updating}
                className={`
                  ${status.bgClass}
                  p-3 sm:p-4 rounded-lg text-left
                  transition-all duration-300
                  hover:shadow-md hover:-translate-y-1
                  animate-fade-in opacity-0
                  ${isActive ? 'ring-2 ring-offset-2 ring-light-primary dark:ring-dark-primary' : ''}
                  ${updating ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                style={{
                  animationDelay: `${300 + index * 50}ms`,
                  animationFillMode: 'forwards'
                }}
              >
                <div className="flex items-start justify-between mb-1 sm:mb-2">
                  <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${status.iconClass}`} />
                  {isActive && (
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-light-primary dark:text-dark-primary" />
                  )}
                </div>
                <h4 className={`font-semibold text-sm sm:text-base ${status.textClass} mb-0.5 sm:mb-1`}>
                  {status.label}
                </h4>
                <p className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant line-clamp-2">
                  {status.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Messaggio Personalizzato */}
      <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-3 sm:p-6
        animate-fade-in opacity-0"
        style={{animationDelay: '500ms', animationFillMode: 'forwards'}}>
        <h3 className="text-base sm:text-lg font-semibold text-light-on-surface dark:text-dark-on-surface mb-2 sm:mb-4">
          Messaggio Personalizzato
        </h3>
        <div className="flex gap-2 sm:gap-3">
          <input
            type="text"
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Es: Torno tra 30 minuti"
            className="flex-1 px-3 py-2 sm:px-4 sm:py-2 rounded-lg text-sm sm:text-base
              bg-light-surface dark:bg-dark-surface
              text-light-on-surface dark:text-dark-on-surface
              border border-light-outline dark:border-dark-outline
              focus:outline-none"
          />
          {customMessage && (
            <button
              onClick={() => setCustomMessage('')}
              className="px-3 py-2 sm:px-4 sm:py-2 rounded-lg
                bg-light-error/20 dark:bg-dark-error/20
                text-light-error dark:text-dark-error
                hover:bg-light-error/30 dark:hover:bg-dark-error/30
                transition-colors"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Stati Personalizzati */}
      <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-3 sm:p-6
        animate-fade-in opacity-0"
        style={{animationDelay: '600ms', animationFillMode: 'forwards'}}>
        {/* Header con layout responsive */}
        <div className="mb-3 sm:mb-4">
          {/* Titolo con icona e badge */}
          <div className="flex items-center gap-2 mb-3 sm:mb-0">
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-light-primary dark:text-dark-primary" />
            <h3 className="text-base sm:text-lg font-semibold text-light-on-surface dark:text-dark-on-surface">
              Stati Personalizzati
            </h3>
            {customStatuses.length > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-light-primary/20 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary">
                {customStatuses.length}
              </span>
            )}
          </div>
          
          {/* Pulsanti - sotto su mobile, inline su desktop */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2 sm:justify-end sm:absolute sm:top-4 sm:right-4">
            {customStatuses.length > 0 && (
              <button
                onClick={handleDeleteAllCustomStatuses}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm
                  bg-light-error/10 dark:bg-dark-error/10
                  text-light-error dark:text-dark-error
                  hover:bg-light-error/20 dark:hover:bg-dark-error/20 transition-colors"
                title="Elimina tutti gli stati"
              >
                <Trash2 className="w-4 h-4" />
                <span>Elimina tutti</span>
              </button>
            )}
            <button
              onClick={() => {
                setIsAddingCustom(!isAddingCustom);
                if (isAddingCustom) {
                  setEditingCustomStatus(null);
                  setNewCustomStatus({ label: '', message: '', icon: 'Star', color: '#EC4899' });
                }
              }}
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm
                bg-light-primary dark:bg-dark-primary
                text-light-on-primary dark:text-dark-on-primary
                hover:opacity-90 transition-opacity"
            >
              {isAddingCustom ? (
                <><X className="w-4 h-4" /><span>Chiudi</span></>
              ) : (
                <><Plus className="w-4 h-4" /><span>Aggiungi Stato</span></>
              )}
            </button>
          </div>
        </div>

        {/* Form per aggiungere/modificare stato */}
        {isAddingCustom && (
          <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-light-surface dark:bg-dark-surface rounded-lg space-y-3 sm:space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-1 h-6 rounded-full ${editingCustomStatus ? 'bg-light-tertiary dark:bg-dark-tertiary' : 'bg-light-primary dark:bg-dark-primary'}`} />
              <h4 className="font-semibold text-light-on-surface dark:text-dark-on-surface">
                {editingCustomStatus ? 'Modifica Stato Personalizzato' : 'Nuovo Stato Personalizzato'}
              </h4>
            </div>
            <input
              type="text"
              value={newCustomStatus.label}
              onChange={(e) => setNewCustomStatus({...newCustomStatus, label: e.target.value})}
              placeholder="Nome stato (es: Gaming, Relax...)"
              className="w-full px-3 py-2 sm:px-4 sm:py-2 rounded-lg text-sm sm:text-base
                bg-light-surface-container dark:bg-dark-surface-container
                text-light-on-surface dark:text-dark-on-surface
                border border-light-outline dark:border-dark-outline
                focus:outline-none"
            />
            
            <input
              type="text"
              value={newCustomStatus.message}
              onChange={(e) => setNewCustomStatus({...newCustomStatus, message: e.target.value})}
              placeholder="Messaggio (opzionale)"
              className="w-full px-3 py-2 sm:px-4 sm:py-2 rounded-lg text-sm sm:text-base
                bg-light-surface-container dark:bg-dark-surface-container
                text-light-on-surface dark:text-dark-on-surface
                border border-light-outline dark:border-dark-outline
                focus:outline-none"
            />

            {/* Selettore Icone */}
            <div>
              <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                Scegli un'icona
              </label>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                {availableIcons.map((iconItem) => {
                  const IconComponent = iconItem.component;
                  return (
                    <button
                      key={iconItem.name}
                      type="button"
                      onClick={() => {
                        console.log('🎨 Icon selected:', iconItem.name);
                        setNewCustomStatus({...newCustomStatus, icon: iconItem.name});
                      }}
                      className={`p-3 rounded-lg transition-all ${
                        newCustomStatus.icon === iconItem.name
                          ? 'bg-light-primary dark:bg-dark-primary ring-2 ring-light-primary dark:ring-dark-primary'
                          : 'bg-light-surface-container dark:bg-dark-surface-container hover:bg-light-surface-variant dark:hover:bg-dark-surface-variant'
                      }`}
                    >
                      <IconComponent 
                        className={`w-5 h-5 mx-auto ${
                          newCustomStatus.icon === iconItem.name
                            ? 'text-light-on-primary dark:text-dark-on-primary'
                            : 'text-light-on-surface dark:text-dark-on-surface'
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selettore Colori */}
            <div>
              <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                Scegli un colore
              </label>
              <div className="grid grid-cols-8 sm:grid-cols-16 gap-2">
                {availableColors.map((colorItem) => (
                  <button
                    key={colorItem.name}
                    type="button"
                    onClick={() => setNewCustomStatus({...newCustomStatus, color: colorItem.value})}
                    className={`w-8 h-8 rounded-full transition-all flex items-center justify-center ${
                      newCustomStatus.color === colorItem.value
                        ? 'ring-2 ring-offset-2 ring-light-primary dark:ring-dark-primary'
                        : 'hover:scale-110'
                    }`}
                    style={{ backgroundColor: colorItem.value }}
                    title={colorItem.name}
                  >
                    {newCustomStatus.color === colorItem.value && (
                      <Check className="w-4 h-4 text-white" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={editingCustomStatus ? handleEditCustomStatus : handleAddCustomStatus}
                disabled={!newCustomStatus.label.trim()}
                className="flex-1 px-3 py-2 sm:px-4 sm:py-2 rounded-lg text-sm sm:text-base
                  bg-light-primary dark:bg-dark-primary
                  text-light-on-primary dark:text-dark-on-primary
                  hover:opacity-90 transition-opacity font-medium
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingCustomStatus ? 'Aggiorna Stato' : 'Salva Stato'}
              </button>
              <button
                onClick={() => {
                  setIsAddingCustom(false);
                  setEditingCustomStatus(null);
                  setNewCustomStatus({ label: '', message: '', icon: 'Star', color: '#EC4899' });
                }}
                className="flex-1 px-3 py-2 sm:px-4 sm:py-2 rounded-lg text-sm sm:text-base
                  bg-light-surface-variant dark:bg-dark-surface-variant
                  text-light-on-surface-variant dark:text-dark-on-surface-variant
                  hover:opacity-90 transition-opacity font-medium"
              >
                Annulla
              </button>
            </div>
          </div>
        )}

        {/* Lista stati personalizzati */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
          {customStatuses.map((status, index) => {
            const isActive = currentStatus === 'custom' && status.id === currentStatus;
            const StatusIcon = availableIcons.find(i => i.name === status.icon)?.component || Star;
            const statusColor = status.color || '#EC4899';
            
            return (
              <div
                key={status.id}
                className={`
                  bg-light-surface dark:bg-dark-surface
                  p-3 sm:p-4 rounded-lg
                  transition-all duration-300
                  animate-fade-in opacity-0
                  ${isActive ? 'ring-2 ring-light-primary dark:ring-dark-primary' : ''}
                `}
                style={{
                  animationDelay: `${700 + index * 50}ms`,
                  animationFillMode: 'forwards',
                  borderLeft: `4px solid ${statusColor}`
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${statusColor}20` }}
                    >
                      <StatusIcon 
                        className="w-5 h-5" 
                        style={{ color: statusColor }}
                      />
                    </div>
                    <h4 className="font-semibold text-sm sm:text-base text-light-on-surface dark:text-dark-on-surface">
                      {status.label}
                    </h4>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => startEditCustomStatus(status)}
                      className="text-light-primary dark:text-dark-primary hover:bg-light-primary/10 dark:hover:bg-dark-primary/10 p-1 rounded transition-colors"
                      title="Modifica stato"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCustomStatus(status.id)}
                      className="text-light-error dark:text-dark-error hover:bg-light-error/10 dark:hover:bg-dark-error/10 p-1 rounded transition-colors"
                      title="Elimina stato"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {status.message && (
                  <p className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant mb-2 sm:mb-3">
                    {status.message}
                  </p>
                )}
                <button
                  onClick={() => handleStatusChange('custom', status.id)}
                  disabled={updating}
                  className={`
                    w-full px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium
                    transition-all duration-300
                    ${isActive 
                      ? 'bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary'
                      : 'bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface hover:bg-light-surface-variant dark:hover:bg-dark-surface-variant'
                    }
                    ${updating ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  {isActive ? 'Attivo' : 'Attiva'}
                </button>
              </div>
            );
          })}
        </div>

        {customStatuses.length === 0 && !isAddingCustom && (
          <div className="text-center py-6 sm:py-8 text-light-on-surface-variant dark:text-dark-on-surface-variant text-sm">
            <p>Nessuno stato personalizzato. Creane uno!</p>
          </div>
        )}
      </div>

      {/* Programmazione Stati Automatica */}
      <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-3 sm:p-6
        animate-fade-in opacity-0"
        style={{animationDelay: '700ms', animationFillMode: 'forwards'}}>
        {/* Header con layout responsive */}
        <div className="mb-3 sm:mb-4">
          {/* Titolo e descrizione */}
          <div className="mb-3 sm:mb-0">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-light-primary dark:text-dark-primary" />
              <h3 className="text-base sm:text-lg font-semibold text-light-on-surface dark:text-dark-on-surface">
                Programmazione Stati
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
              Configura cambi di stato automatici basati su orari e giorni della settimana
            </p>
          </div>
          
          {/* Pulsante - sotto su mobile, inline su desktop */}
          <div className="sm:absolute sm:top-4 sm:right-4">
            <button
              onClick={() => { resetScheduleForm(); setShowScheduleModal(true); }}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm
                bg-light-primary dark:bg-dark-primary
                text-light-on-primary dark:text-dark-on-primary
                hover:opacity-90 transition-opacity"
            >
              <Plus className="w-4 h-4" />
              <span>Nuova Programmazione</span>
            </button>
          </div>
        </div>

        {/* Lista Programmazioni */}
        <div className="space-y-3 sm:space-y-4">
          {schedules.length === 0 ? (
            <div className="bg-light-surface dark:bg-dark-surface p-6 sm:p-8 rounded-lg text-center">
              <Calendar className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-light-on-surface-variant dark:text-dark-on-surface-variant opacity-50" />
              <p className="text-sm sm:text-base text-light-on-surface-variant dark:text-dark-on-surface-variant">
                Nessuna programmazione configurata
              </p>
            </div>
          ) : (
            schedules.map((schedule, index) => (
              <div
                key={schedule.id}
                className={`bg-light-surface dark:bg-dark-surface rounded-lg shadow-surface-container p-3 sm:p-4 transition-all hover:shadow-lg
                  animate-fade-in opacity-0
                  ${!schedule.isActive ? 'opacity-60' : ''}`}
                style={{
                  animationDelay: `${800 + index * 50}ms`,
                  animationFillMode: 'forwards'
                }}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                      <h4 className="text-base sm:text-lg font-semibold text-light-on-surface dark:text-dark-on-surface">
                        {schedule.scheduleName}
                      </h4>
                      {schedule.isActiveNow && (
                        <span className="px-2 py-0.5 sm:py-1 bg-green-500/20 text-green-600 dark:text-green-400 text-xs font-medium rounded-full whitespace-nowrap">
                          ATTIVO ORA
                        </span>
                      )}
                      {!schedule.isActive && (
                        <span className="px-2 py-0.5 sm:py-1 bg-light-surface-variant dark:bg-dark-surface-variant text-light-on-surface-variant dark:text-dark-on-surface-variant text-xs font-medium rounded-full whitespace-nowrap">
                          DISATTIVATO
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mt-3">
                      <div>
                        <p className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1">
                          Stato:
                        </p>
                        <div className="flex items-center gap-2">
                          {schedule.status === 'custom' && schedule.CustomStatus ? (
                            <>
                              <div 
                                className="w-2 h-2 sm:w-3 sm:h-3 rounded-full" 
                                style={{ backgroundColor: schedule.CustomStatus.color || '#EC4899' }}
                              ></div>
                              <span className="font-medium text-sm sm:text-base text-light-on-surface dark:text-dark-on-surface">
                                {schedule.CustomStatus.label}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${getStatusColorForSchedule(schedule.status)}`}></span>
                              <span className="font-medium text-sm sm:text-base text-light-on-surface dark:text-dark-on-surface">
                                {getStatusLabelForSchedule(schedule.status)}
                              </span>
                            </>
                          )}
                        </div>
                        {schedule.customMessage && (
                          <p className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant mt-1 italic">
                            "{schedule.customMessage}"
                          </p>
                        )}
                      </div>

                      <div>
                        <p className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1">
                          Orario:
                        </p>
                        <div className="flex items-center gap-2 text-light-on-surface dark:text-dark-on-surface">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="font-medium text-sm sm:text-base">
                            {schedule.startTime} - {schedule.endTime}
                          </span>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1">
                          Giorni:
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {schedule.daysOfWeek.map(day => (
                            <span
                              key={day}
                              className="px-1.5 py-0.5 sm:px-2 sm:py-0.5 bg-light-primary/20 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary text-xs font-medium rounded"
                            >
                              {getDayLabel(day)}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1">
                          Prossima Esecuzione:
                        </p>
                        <p className="text-xs sm:text-sm font-medium text-light-on-surface dark:text-dark-on-surface">
                          {formatNextExecution(schedule)}
                        </p>
                      </div>
                    </div>

                    {schedule.restorePreviousStatus && (
                      <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm text-light-primary dark:text-dark-primary">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Ripristina stato precedente alla fine</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleToggleScheduleActive(schedule)}
                      className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                        schedule.isActive
                          ? 'bg-green-500/20 text-green-600 dark:text-green-400 hover:bg-green-500/30'
                          : 'bg-light-surface-variant dark:bg-dark-surface-variant text-light-on-surface-variant dark:text-dark-on-surface-variant hover:bg-light-surface-container-high dark:hover:bg-dark-surface-container-high'
                      }`}
                      title={schedule.isActive ? 'Disattiva' : 'Attiva'}
                    >
                      {schedule.isActive ? <Power className="w-4 h-4 sm:w-5 sm:h-5" /> : <PowerOff className="w-4 h-4 sm:w-5 sm:h-5" />}
                    </button>
                    <button
                      onClick={() => handleEditSchedule(schedule)}
                      className="p-1.5 sm:p-2 bg-light-primary/20 dark:bg-dark-primary/20 text-light-primary dark:text-dark-primary rounded-lg hover:bg-light-primary/30 dark:hover:bg-dark-primary/30 transition-colors"
                      title="Modifica"
                    >
                      <Edit2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteSchedule(schedule.id)}
                      className="p-1.5 sm:p-2 bg-light-error/20 dark:bg-dark-error/20 text-light-error dark:text-dark-error rounded-lg hover:bg-light-error/30 dark:hover:bg-dark-error/30 transition-colors"
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal per Programmazione */}
      <Modal
        isOpen={showScheduleModal}
        onClose={() => { setShowScheduleModal(false); resetScheduleForm(); }}
        title={editingSchedule ? 'Modifica Programmazione' : 'Nuova Programmazione'}
        icon={Calendar}
        iconColor="text-light-primary dark:text-dark-primary"
        size="lg"
      >
        <form onSubmit={handleScheduleSubmit} className="space-y-4 sm:space-y-6">
                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                    Nome Programmazione *
                  </label>
                  <input
                    type="text"
                    value={scheduleFormData.scheduleName}
                    onChange={(e) => setScheduleFormData({ ...scheduleFormData, scheduleName: e.target.value })}
                    className="w-full px-3 py-2 sm:px-4 sm:py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none focus:border-light-primary dark:focus:border-dark-primary text-sm sm:text-base"
                    placeholder="es. Pausa Pranzo"
                  />
                </div>

                {/* Stato */}
                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                    Stato da Applicare *
                  </label>
                  <Select
                    value={scheduleFormData.status}
                    onChange={(value) => setScheduleFormData({ ...scheduleFormData, status: value, customStatusId: '' })}
                    options={[
                      { value: 'disponibile', label: 'Disponibile' },
                      { value: 'occupato', label: 'Occupato' },
                      { value: 'non-disturbare', label: 'Non Disturbare' },
                      { value: 'afk', label: 'AFK' },
                      { value: 'in-pausa', label: 'In Pausa' },
                      ...(customStatuses.length > 0 ? [{ value: 'custom', label: 'Stato Personalizzato' }] : [])
                    ]}
                    placeholder="Seleziona stato..."
                  />
                </div>

                {/* Custom Status Selector */}
                {scheduleFormData.status === 'custom' && (
                  <div>
                    <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                      Seleziona Stato Personalizzato *
                    </label>
                    <Select
                      value={scheduleFormData.customStatusId}
                      onChange={(value) => setScheduleFormData({ ...scheduleFormData, customStatusId: value })}
                      options={customStatuses.map(cs => ({
                        value: cs.id.toString(),
                        label: cs.label
                      }))}
                      placeholder="Seleziona stato personalizzato..."
                    />
                  </div>
                )}

                {/* Messaggio Custom */}
                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                    Messaggio Personalizzato (opzionale)
                  </label>
                  <input
                    type="text"
                    value={scheduleFormData.customMessage}
                    onChange={(e) => setScheduleFormData({ ...scheduleFormData, customMessage: e.target.value })}
                    className="w-full px-3 py-2 sm:px-4 sm:py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none focus:border-light-primary dark:focus:border-dark-primary text-sm sm:text-base"
                    placeholder="es. Torno alle 14:00"
                  />
                </div>

                {/* Giorni della Settimana */}
                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                    Giorni della Settimana *
                  </label>
                  
                  {/* Pulsanti azioni rapide */}
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setScheduleFormData({ ...scheduleFormData, daysOfWeek: [0, 1, 2, 3, 4, 5, 6] })}
                      className="flex-1 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium
                        bg-light-primary/10 dark:bg-dark-primary/10
                        text-light-primary dark:text-dark-primary
                        hover:bg-light-primary/20 dark:hover:bg-dark-primary/20
                        transition-colors"
                    >
                      Tutti i giorni
                    </button>
                    <button
                      type="button"
                      onClick={() => setScheduleFormData({ ...scheduleFormData, daysOfWeek: [] })}
                      className="flex-1 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium
                        bg-light-surface-variant dark:bg-dark-surface-variant
                        text-light-on-surface-variant dark:text-dark-on-surface-variant
                        hover:bg-light-surface-container-high dark:hover:bg-dark-surface-container-high
                        transition-colors"
                    >
                      Nessun giorno
                    </button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map(day => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleDay(day.value)}
                        className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                          scheduleFormData.daysOfWeek.includes(day.value)
                            ? 'bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary'
                            : 'bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface-variant dark:text-dark-on-surface-variant hover:bg-light-surface-container-high dark:hover:bg-dark-surface-container-high'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Orari */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                      Ora Inizio *
                    </label>
                    <input
                      type="time"
                      value={scheduleFormData.startTime}
                      onChange={(e) => setScheduleFormData({ ...scheduleFormData, startTime: e.target.value })}
                      className="w-full px-3 py-2 sm:px-4 sm:py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none focus:border-light-primary dark:focus:border-dark-primary text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                      Ora Fine *
                    </label>
                    <input
                      type="time"
                      value={scheduleFormData.endTime}
                      onChange={(e) => setScheduleFormData({ ...scheduleFormData, endTime: e.target.value })}
                      className="w-full px-3 py-2 sm:px-4 sm:py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none focus:border-light-primary dark:focus:border-dark-primary text-sm sm:text-base"
                    />
                  </div>
                </div>

                {/* Opzioni */}
                <div className="space-y-3">
                  <CheckBox
                    checked={scheduleFormData.isActive}
                    onChange={(checked) => setScheduleFormData({ ...scheduleFormData, isActive: checked })}
                    label="Programmazione attiva"
                  />

                  <CheckBox
                    checked={scheduleFormData.restorePreviousStatus}
                    onChange={(checked) => setScheduleFormData({ ...scheduleFormData, restorePreviousStatus: checked })}
                    label="Ripristina stato precedente alla fine della programmazione"
                  />
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowScheduleModal(false); resetScheduleForm(); }}
                    className="px-4 py-2 sm:px-6 sm:py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg hover:bg-light-surface-container-high dark:hover:bg-dark-surface-container-high transition-colors text-sm sm:text-base"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 sm:px-6 sm:py-2 bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary rounded-lg hover:opacity-90 transition-opacity text-sm sm:text-base"
                  >
                    {editingSchedule ? 'Aggiorna' : 'Crea'}
                  </button>
                </div>
              </form>
            </Modal>
      </div>
    </div>
  );
};

export default StatusManagement;
